import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import axios from "axios";
import { process } from "@progress/kendo-data-query";
import {
    Grid,
    GridColumn as Col,
    GridNoRecords,
    GridToolbar,
} from "@progress/kendo-react-grid";
import { Input } from "@progress/kendo-react-inputs";
import CryptoJS from "crypto-js";

import "./Login.scss";
import TopModal from "../../components/common/modals/TopModal";
import { ApiCalls, getUrl } from "../../api/allapiCall";
import { generateMachineName, showErrorMessage, store } from "../../utils";
import { appName } from "../../utils";
import { API_ROOT, SECRET_KEY } from "../../api/apiRoot";
import { IconButton } from "@material-ui/core";
import { initData, initTableState, SelectCustomerCell } from "./loginHelpers";
import { connect } from "react-redux";
import { addDataToStore } from "../../state/actions";

function AdminModal({
    openModal,
    closeModal,
    login,
    openModalFromNavBar,
    loading,
    setLoading,
    addDataToStore,
}) {
    const [searchInput, setSearchInput] = useState("");
    const [message, setMessage] = useState("");
    const [selectedRow, setSelectedRow] = useState(null);
    const [tableState, setTableState] = useState(initTableState);
    const [data, setData] = useState(initData);
    const [searchedData, setSearchedData] = useState([]);
    const [origData, setOrigData] = useState([]);
    const [componentMounted, setCompMounted] = useState(false);
    const [filterValue, setFilterValue] = useState("");
    const [sikkaAdminToken, setSikkaAdminToken] = useState(
        store.get("sikkaAdminToken"),
    );
    const ipDetails = store.get("ip");
    const sikkaAdminCred = store.get("sikkaAdminCred");

    const relogin = async () => {
        const bytes = CryptoJS.AES.decrypt(
            sikkaAdminCred.encryptedPw,
            SECRET_KEY,
        );
        const originalPassword = bytes.toString(CryptoJS.enc.Utf8);
        const res = await ApiCalls.login({
            username: sikkaAdminCred.username,
            password: originalPassword,
            publicIP: ipDetails.ip,
            machineName: generateMachineName(),
        });
        setSikkaAdminToken(res?.apiToken?.token);
    };

    useEffect(() => {
        if (!openModalFromNavBar) return;
        relogin(); // get new token in case the modal is opened from Navbar
    }, []);

    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        if (!componentMounted) setCompMounted(true);
        const trimmedInput = searchInput.trim();
        if (!trimmedInput) {
            setMessage("Input is required");
            return;
        }

        // call api
        try {
            setLoading(true);
            setSelectedRow(null);
            setMessage("");
            setFilterValue("");

            const headers = {
                "x-auth-token": `Bearer ${sikkaAdminToken}`,
                "Content-Type": "application/json",
            };

            axios
                .get(
                    getUrl("/Auth/LicenseCustomerData", {
                        searchBy: trimmedInput,
                    }),
                    { headers },
                )
                .then((res) => {
                    if (res?.data) {
                        setTableState(initTableState);
                        setData(process(res.data, initTableState));
                        setOrigData(res.data);
                        setSearchedData(res.data);
                    }
                })
                .catch(async (err) => {
                    console.log(err);
                    if (err.response && err.response.status === 400) {
                        const errorMessage =
                            err.response.data?.message || "An error occurred.";
                        if (errorMessage === "Auth token expired.") {
                            if (openModalFromNavBar) {
                                await relogin();
                                handleSearch();
                            } else {
                                showErrorMessage(
                                    "Auth Token Expired. Please re-login",
                                );
                                closeModal();
                            }
                        } else {
                            showErrorMessage(errorMessage);
                        }
                    } else {
                        // Handle other errors
                        showErrorMessage("Something went wrong.");
                    }
                });
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectCustomer = async () => {
        try {
            setLoading(true);
            const headers = {
                "x-auth-token": `Bearer ${selectedRow?.apiToken?.token}`,
                "Content-Type": "application/json",
            };

            const params = {
                masterId: selectedRow?.masterCustomerID,
                userId: selectedRow?.userID,
                requestStartTime: selectedRow?.apiToken?.requestStartTime,
                expiryUtcTime: selectedRow?.apiToken?.expiryUtcTime,
                publicIP: ipDetails.ip,
                userName: sikkaAdminCred.username,
                appName: appName,
                apiToken: selectedRow?.apiToken?.token,
                machineName: generateMachineName(),
            };
            const url = `${API_ROOT}/Auth/JumpCloudAuthDetails`;
            const res = await axios.post(url, params, {
                headers,
            });
            if (!res?.data?.isSuccess || res?.data?.message !== "Successful") {
                showErrorMessage("You are not authorized.");
                setLoading(false);
            } else {
                store.set("session", "sikkaAdmin", true);
                addDataToStore("sikkaAdmin", true);
                login(selectedRow);
            }
        } catch (err) {
            console.log(err);
            setLoading(false);
        }
    };

    const dataStateChange = (event) => {
        setTableState(event.dataState);
        setData(process(searchedData, event.dataState));
    };

    const onFilterChange = (ev) => {
        let value = ev.value;
        setFilterValue(ev.value);
        let newData = origData.filter((item) => {
            let match = false;
            for (const property in item) {
                if (
                    item[property]
                        .toString()
                        .toLocaleLowerCase()
                        .indexOf(value.toLocaleLowerCase()) >= 0
                ) {
                    match = true;
                }
                if (
                    item[property].toLocaleDateString &&
                    item[property].toLocaleDateString().indexOf(value) >= 0
                ) {
                    match = true;
                }
            }
            return match;
        });
        setData(process(newData, tableState));
        setSearchedData(newData);
    };

    const handleRowClick = (e) => {
        setSelectedRow(e.dataItem);
    };

    return (
        <TopModal
            openModal={openModal}
            closeModal={closeModal}
            animationOff={true}
            noScrollToBottom={true}
        >
            <div
                style={{ width: "min(1400px, 95vw)" }}
                className="px-4 pt-2 pb-4 position-relative AdminModal"
            >
                {loading && (
                    <div
                        className="position-absolute d-flex justify-content-center align-items-center"
                        style={{
                            background: "white",
                            opacity: 0.5,
                            zIndex: 1,
                            width: "calc(100% - 20px)",
                            height: "100%",
                        }}
                    >
                        <img
                            alt="Loading..."
                            src="https://s3.amazonaws.com/dpm2.0/Icons/Spinner_purple.gif"
                            width={100}
                            height={100}
                        />
                    </div>
                )}
                <h1 style={{ fontSize: 24 }}>Search for a Customer</h1>

                <form onSubmit={handleSearch} method="POST">
                    <div className="d-flex gap-3 flex-wrap">
                        <div
                            className="position-relative flex-1"
                            style={{
                                maxWidth: 500,
                                minWidth: 400,
                            }}
                        >
                            <input
                                style={{
                                    paddingLeft: 16,
                                    paddingRight: 40,
                                    height: 46,
                                }}
                                required
                                className="primary-input w-100"
                                name="searchInput"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                            {searchInput.trim() ? (
                                <IconButton
                                    onClick={() => setSearchInput("")}
                                    style={{
                                        position: "absolute",
                                        right: 5,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#3B4758",
                                    }}
                                    size="small"
                                >
                                    <ClearIcon
                                        style={{
                                            width: 28,
                                            height: 28,
                                            color: "#3B4758",
                                        }}
                                    />
                                </IconButton>
                            ) : (
                                <SearchIcon
                                    style={{
                                        width: 28,
                                        height: 28,
                                        position: "absolute",
                                        top: 9,
                                        right: 8,
                                        color: "#3B4758",
                                    }}
                                />
                            )}
                        </div>

                        <button
                            className="btn primary-btn adminModal-btn"
                            type="submit"
                            disabled={
                                loading || searchInput.trim().length === 0
                            }
                        >
                            Search Customer
                        </button>
                    </div>
                </form>

                {message && <div className="text-red-clr">{message}</div>}

                {componentMounted && (
                    <div className="mt-4 admin-modal-table scroll-x w-100">
                        <Grid
                            style={{
                                borderRadius: 6,
                                borderColor: "transparent",
                            }}
                            data={data}
                            onDataStateChange={dataStateChange}
                            {...tableState}
                            resizable={true}
                            sortable={true}
                            scrollable="scrollable"
                            onRowClick={handleRowClick}
                        >
                            <GridNoRecords>
                                {loading
                                    ? "Loading Data..."
                                    : "No Data Available"}
                            </GridNoRecords>
                            <GridToolbar>
                                <div>
                                    <span
                                        style={{
                                            padding: "5px",
                                        }}
                                    >
                                        Search:{" "}
                                    </span>
                                    <span>
                                        <Input
                                            value={filterValue}
                                            onChange={onFilterChange}
                                            style={{
                                                border: "2px solid #ccc",
                                                boxShadow:
                                                    "inset 0px 0px 0.5px 0px rgba(0,0,0,0.0.1)",
                                            }}
                                            placeholder="Search by Email, Name, Master Customer ID..."
                                        />
                                    </span>
                                </div>
                            </GridToolbar>

                            <Col
                                title="Select"
                                width={90}
                                cell={(props) => (
                                    <SelectCustomerCell
                                        {...props}
                                        selectedRow={selectedRow}
                                        setSelectedRow={setSelectedRow}
                                    />
                                )}
                                sortable={false}
                            />
                            <Col
                                title="Email Address"
                                field="userEmail"
                                width={300}
                            />
                            <Col title="First Name" field="userFName" />
                            <Col title="Last Name" field="userLName" />
                            <Col title="Address" field="address" />
                            <Col title="Speciality" field="speciality" />
                            <Col title="User Type" field="user_Type" />
                            <Col
                                title="Master Customer ID"
                                field="masterCustomerID"
                            />
                        </Grid>
                    </div>
                )}
                {selectedRow && (
                    <div className="mt-4 w-100 d-flex justify-content-end">
                        <button
                            className="btn primary-btn fz-18 fw-bold"
                            style={{ padding: "8px 32px" }}
                            disabled={!selectedRow}
                            onClick={handleSelectCustomer}
                        >
                            Select Customer
                        </button>
                    </div>
                )}
            </div>
        </TopModal>
    );
}

export default connect(null, { addDataToStore })(AdminModal);
